import { useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  addDays,
  describeCadence,
  streakUnit,
  type LocalDate,
  type ProgressionState,
} from '@selflevel/domain';
import { Marca } from '@selflevel/ui';
import {
  useAreaMap,
  useMood,
  useProgression,
  useSettings,
  useTodayHabits,
  useToday,
} from '../store/hooks';
import { setHabitStatus } from '../store/mutations';
import type { TodayHabit } from '../store/selectors';
import { zonaDelDispositivo } from '../store/session';
import {
  Bar,
  Card,
  Empty,
  Insignia,
  Metrica,
  Sello,
  fechaLarga,
  num,
  useReciente,
  useSubida,
} from '../ui/primitivas';
import { IconCheck, IconMas, IconSkip } from '../ui/iconos';
import { EditorHabito } from './editor-habito';
import { saludo, tramoHaciaElSiguiente } from './hoy-cabecera';
import { nombreDelDia } from '../ui/tira';
import { AnimoRapido } from './animo';
import { OfrecerAvisos } from './hoy-avisos';
import { LoDificilPrimero, TareasDeHoy, TarjetaRitual } from './hoy-intencion';

/**
 * HOY — la pantalla que se abre a diario, y la única que tiene que funcionar a
 * la primera con una mano y sin pensar.
 *
 * Tres decisiones que la ordenan:
 *
 *  1. **Lo que toca hoy, y nada más.** Un hábito que hoy no toca no sale ni en
 *     gris ni tachado: sale la lista de lo que toca, y por eso se puede
 *     terminar. Una lista que nunca se vacía no es una lista, es un reproche.
 *
 *  2. **Marcar es un toque.** El control es un círculo de 44 px de área táctil.
 *     Omitir a propósito existe y está a un toque más, porque «hoy no» y «se me
 *     olvidó» no son lo mismo, y la app no puede tratarlos igual.
 *
 *  3. **La acción tiene consecuencia visible.** Marcar sube unos puntos, y hasta
 *     ahora eso ocurría en silencio: la cifra pasaba de 78 a 93 sin que nada
 *     dijera que había pasado algo. Ahora el `+15` sube y se apaga sobre el
 *     número, y debajo está lo único que de verdad engancha en una app así:
 *     saber exactamente qué falta para lo siguiente y que parezca poco.
 *
 * En escritorio se parte en dos columnas, y no por tener sitio: son dos cosas
 * distintas. A la izquierda lo que hay que hacer; a la derecha el estado del día
 * —la temporada, cómo estás— que en un monitor puede quedarse a la vista
 * mientras la lista corre.
 */
export function Hoy(): ReactElement {
  const today = useToday();
  const settings = useSettings();
  const habits = useTodayHabits();
  const areas = useAreaMap();
  const progression = useProgression();
  const mood = useMood(today);
  const [editor, setEditor] = useState(false);

  // Omitir a propósito es una decisión, no algo pendiente: un omitido sale de la
  // cuenta en vez de quedarse como «por hacer» para siempre. Antes contaba en el
  // total y no en los hechos, y con uno omitido «Hoy está hecho» no salía nunca.
  const lista = habits ?? [];
  const hechos = lista.filter((h) => h.entry?.status === 'done').length;
  const omitidos = lista.filter((h) => h.entry?.status === 'skipped').length;
  const total = lista.length - omitidos;
  const puntosHoy = progression?.byDay.get(today) ?? 0;
  const todoHecho = lista.length > 0 && hechos === total;
  // Se marca con la zona en la que está el dispositivo, no con la del alta.
  const zona = zonaDelDispositivo(settings.tz);
  // `null` mientras la progresión no ha llegado: sin eso, el salto de 0 a los
  // puntos del día al terminar de cargar se lee como una subida y la app te
  // felicita por abrirla.
  const subida = useSubida(progression ? puntosHoy : null);

  return (
    <main className="screen screen--wide">
      {/* SIN FRANJA. El saludo y el nivel flotan sobre el fondo: el nivel es lo que
          esta app promete, y una banda gris encima lo tapaba en vez de enmarcarlo. */}
      <header className="hoy-cabecera">
        <div className="hoy-cabecera__fila">
          <div className="hoy-cabecera__saludo">
            <p className="t-sm t-dim hoy-cabecera__fecha">
              {nombreDelDia(today)} {fechaLarga(today, false)}
            </p>
            <h1 className="hoy-cabecera__titulo">{saludo(settings.displayName)}</h1>
          </div>
          <Link to="/progreso" aria-label="Ver progreso" className="hoy-cabecera__nivel">
            <Insignia
              size={60}
              nivel={progression?.generalLevel ?? '·'}
              /*
                Sin el `|| 1` de antes: con una media exacta —una cuenta recién
                hecha— el anillo entero diría «te falta un suspiro» con cero puntos.
              */
              progress={progression ? progression.generalMean % 1 : 0}
              segmentos={progression?.dimensions.length ?? 0}
              label={`Nivel ${progression?.generalLevel ?? 0}`}
            />
          </Link>
        </div>
        {progression ? (
          <div className="hoy-cabecera__tramo">
            <Bar
              progress={
                tramoHaciaElSiguiente(progression.generalLevel, progression.generalMean).fraccion
              }
              label="Progreso hacia el siguiente nivel"
            />
            <span className="t-sm t-dim">
              {tramoHaciaElSiguiente(progression.generalLevel, progression.generalMean).texto}
            </span>
          </div>
        ) : null}
      </header>

      <div className="cols">
        <div>
          <OfrecerAvisos />
          <TarjetaRitual today={today} tz={zona} />

          {/* ═══════════════════════════════════════════════════════════════
              LA CABECERA DEL DÍA.

              Era una cifra plana y una barra fina, y contaba exactamente lo
              mismo que cuenta ahora. La diferencia es que ahora se ve: el
              anillo de veinticinco muescas se rellena al entrar, la cifra sube
              hasta su valor en vez de aparecer puesta, y cada hábito que marcas
              mueve las dos cosas.

              No es maquillaje sobre un dato: es el dato, con el tamaño que le
              corresponde a lo único que esta pantalla te pide que hagas.
              ═══════════════════════════════════════════════════════════════ */}
          <Metrica
            titulo="Hoy"
            valor={hechos}
            total={total}
            estado={
              lista.length === 0
                ? 'sin hábitos'
                : todoHecho
                  ? 'todo hecho'
                  : `${total - hechos} por hacer`
            }
            progress={total === 0 ? (todoHecho ? 1 : 0) : hechos / total}
            pie={
              <span className="puntos">
                <span className="puntos__cifra t-body-strong" data-sube={subida !== null}>
                  {num(puntosHoy)}
                </span>{' '}
                puntos hoy
                {subida !== null ? (
                  <span key={subida} className="puntos__delta t-sm">
                    +{num(subida)}
                  </span>
                ) : null}
              </span>
            }
          />

          <LoDificilPrimero today={today} tz={zona} />

          {habits === undefined ? null : habits.length === 0 ? (
            <Empty
              title="Todavía no hay nada para hoy"
              action={
                <button type="button" className="btn btn--primary" onClick={() => setEditor(true)}>
                  <IconMas size={18} /> Añadir un hábito
                </button>
              }
            >
              Empieza por uno. De verdad: por uno.
            </Empty>
          ) : (
            <ul className="stack">
              {habits.map((item) => (
                <FilaHabito
                  key={item.habit.id}
                  item={item}
                  color={areas.get(item.habit.areaId)?.colorToken ?? '--dim-1'}
                  today={today}
                  tz={zona}
                />
              ))}
            </ul>
          )}

          {todoHecho ? (
            <Card>
              {/* Ni confeti ni exclamaciones. Un hecho, dicho una vez. */}
              <p className="t-title-3">Hoy está hecho.</p>
              <p className="t-sm t-dim" style={{ marginTop: 'var(--sp-2)' }}>
                {num(puntosHoy)} puntos. Mañana vuelve a empezar.
              </p>
            </Card>
          ) : null}

          {habits && habits.length > 0 ? (
            <button
              type="button"
              className="btn btn--ghost btn--block"
              onClick={() => setEditor(true)}
            >
              <IconMas size={18} /> Añadir un hábito
            </button>
          ) : null}

          <TareasDeHoy today={today} tz={zona} />
        </div>

        <div className="cols__lado">
          {progression ? <Siguiente estado={progression} today={today} /> : null}
          {progression ? <Temporada estado={progression} /> : null}
          <AnimoRapido localDate={today} tz={zona} registrado={mood} />
        </div>
      </div>

      <EditorHabito open={editor} onClose={() => setEditor(false)} />
    </main>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LO SIGUIENTE.
 *
 * Esto era una línea de 13 px atenuados que empezaba por «Temporada» y terminaba
 * en «900 para Regular», debajo de una barra. Tenía toda la información y no
 * enganchaba a nadie, por una razón concreta: **900 puntos no significan nada**.
 * Nadie sabe si son dos días o dos meses.
 *
 * Así que la tarjeta dice tres cosas y la tercera es la que importa:
 *   · qué es lo siguiente —y qué abre, si es un hito—,
 *   · cuánto falta,
 *   · **cuántos días es eso a tu ritmo de las dos últimas semanas**.
 *
 * Y se elige el más cercano de los dos objetivos que existen a la vez —el hito
 * del catálogo y la liga de la temporada— porque enseñar los dos convierte una
 * meta en una lista de tareas, y enseñar siempre el mismo hace que uno de los
 * dos se quede semanas sin moverse.
 *
 * La estimación no se maquilla. Si a tu ritmo son tres meses, dice tres meses.
 * Una app que redondea hacia abajo para animarte deja de servir para lo único
 * que sirve, que es saber dónde estás.
 * ═══════════════════════════════════════════════════════════════════════════
 */
function Siguiente({
  estado,
  today,
}: {
  estado: ProgressionState;
  today: LocalDate;
}): ReactElement | null {
  const hito = estado.nextMilestone;
  const liga = estado.season.next;

  // Ninguno de los dos: catálogo terminado y liga máxima. Es raro y es una buena
  // noticia; lo que no puede es dejar una tarjeta vacía.
  if (!hito && !liga) return null;

  const porHito =
    hito !== null && (liga === null || estado.toNextMilestone <= estado.season.toNext);

  const titulo = porHito ? (hito?.name ?? '') : (liga?.name ?? '');
  const nota = porHito ? (hito?.unlocks ?? '') : `Liga de ${estado.season.label}`;
  const falta = porHito ? estado.toNextMilestone : estado.season.toNext;

  const desde = porHito ? (estado.unlocked.at(-1)?.points ?? 0) : estado.season.liga.from;
  const hasta = porHito ? (hito?.points ?? 0) : (liga?.from ?? 0);
  const ahora = porHito ? estado.totalPoints : estado.season.points;
  const tramo = hasta - desde;
  const progreso = tramo > 0 ? (ahora - desde) / tramo : 0;

  const ritmo = ritmoDiario(estado.byDay, today);

  return (
    <Card className="siguiente">
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="grow">
          <p className="t-caption t-dim">Lo siguiente</p>
          <p className="t-title-3">{titulo}</p>
          {nota ? (
            <p className="t-sm t-dim" style={{ marginTop: 2 }}>
              {nota}
            </p>
          ) : null}
        </div>
        {porHito ? null : <Sello fill={liga?.fill ?? 0} size={40} />}
      </div>

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <Bar progress={progreso} label={`Progreso hacia ${titulo}`} />
      </div>

      <p className="row t-sm" style={{ marginTop: 'var(--sp-3)', gap: 'var(--sp-2)' }}>
        <span className="siguiente__falta">{num(falta)}</span>
        <span className="t-dim grow">{falta === 1 ? 'punto' : 'puntos'}</span>
        {ritmo > 0 ? <span className="t-dim">{cuantoFalta(falta, ritmo)}</span> : null}
      </p>
    </Card>
  );
}

/** La temporada en curso: el sello, la liga y lo que queda de trimestre. */
function Temporada({ estado }: { estado: ProgressionState }): ReactElement {
  return (
    <Card>
      <div className="row">
        <Sello
          fill={estado.season.liga.fill}
          size={40}
          label={`${estado.season.label}: ${estado.season.liga.name}`}
        />
        <div className="grow">
          <p className="t-caption t-dim">Temporada {estado.season.label}</p>
          <p className="t-body-strong">{estado.season.liga.name}</p>
        </div>
        <span className="t-sm t-dim tnum">{num(estado.season.points)}</span>
      </div>
      <p className="t-sm t-dim" style={{ marginTop: 'var(--sp-3)' }}>
        Cierra el {fechaLarga(estado.season.to)}. Al cerrar no se resta nada: se guarda.
      </p>
    </Card>
  );
}

/**
 * Puntos por día de las dos últimas semanas, contando los días vacíos.
 *
 * Contarlos es lo correcto y es lo incómodo: si dividiéramos solo entre los días
 * con actividad, alguien que registró dos días de catorce vería un ritmo de
 * campeón y una estimación de tres días para algo que le va a costar un mes.
 */
function ritmoDiario(byDay: ReadonlyMap<LocalDate, number>, today: LocalDate, dias = 14): number {
  let suma = 0;
  for (let i = 0; i < dias; i++) suma += byDay.get(addDays(today, -i)) ?? 0;
  return suma / dias;
}

function cuantoFalta(falta: number, ritmo: number): string {
  const dias = Math.max(1, Math.ceil(falta / ritmo));
  if (dias === 1) return 'un día a tu ritmo';
  if (dias <= 60) return `${dias} días a tu ritmo`;
  const meses = Math.round(dias / 30);
  return `unos ${meses} meses a tu ritmo`;
}

function FilaHabito({
  item,
  color,
  today,
  tz,
}: {
  item: TodayHabit;
  color: string;
  today: string;
  tz: string;
}): ReactElement {
  const estado = item.entry?.status ?? 'pending';
  const hecho = estado === 'done';
  const omitido = estado === 'skipped';
  // Solo cuando lo marcas TÚ, delante. Al abrir la app con cinco ya hechos no se
  // anima nada: `useReciente` arranca con el valor actual y en el montaje no ve
  // ningún cambio.
  const reciente = useReciente(hecho);

  const marcar = (next: 'done' | 'skipped' | 'pending') =>
    void setHabitStatus({ habitId: item.habit.id, localDate: today, status: next, tz });

  const cuota =
    item.habit.cadence.kind === 'timesPerWeek' || item.habit.cadence.kind === 'timesPerMonth';

  return (
    <li className={`habit habit--${estado}`} style={{ ['--area' as string]: `var(${color})` }}>
      <span className="habit__disc">
        <Marca id={item.habit.marca} size={22} polarity={hecho ? 'area' : 'neutra'} />
      </span>

      <Link
        to="/habito/$habitId"
        params={{ habitId: item.habit.id }}
        className="grow habit__enlace"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <span className="habit__name">{item.habit.name}</span>
        {item.habit.description ? (
          <span className="habit__meta habit__descripcion">{item.habit.description}</span>
        ) : null}
        <span className="habit__meta" style={{ display: 'block' }}>
          {cuota
            ? `${item.doneInPeriod} de ${item.target} · ${describeCadence(item.habit.cadence)}`
            : describeCadence(item.habit.cadence)}
          {/* La unidad sale de la cadencia: en los de cuota la racha son semanas o meses. */}
          {item.streak > 1
            ? ` · racha de ${item.streak} ${streakUnit(item.habit.cadence, item.streak)}`
            : ''}
        </span>
      </Link>

      {/* Omitir solo aparece cuando aún no se ha decidido nada: un tercer botón
          permanente convierte una lista en un formulario. */}
      {estado === 'pending' ? (
        <button
          type="button"
          className="habit__check"
          onClick={() => marcar('skipped')}
          aria-label={`Omitir ${item.habit.name} hoy`}
          style={{ width: 36 }}
        >
          <span style={{ width: 22, height: 22, borderStyle: 'dashed' }}>
            <IconSkip size={12} />
          </span>
        </button>
      ) : null}

      <button
        type="button"
        className="habit__check"
        data-reciente={reciente}
        onClick={() => marcar(hecho || omitido ? 'pending' : 'done')}
        aria-pressed={hecho}
        aria-label={hecho ? `Desmarcar ${item.habit.name}` : `Marcar ${item.habit.name} como hecho`}
      >
        <span>{hecho ? <IconCheck /> : omitido ? <IconSkip size={12} /> : null}</span>
      </button>
    </li>
  );
}
